/**
 * The one-time "put this on your home screen" offer.
 *
 * Mounted once, at the root, so it survives the sign-in transition: it shows on the Yahoo login
 * screen and is still the same component — with the same state — after a manager signs in. Tim
 * asked for both, and one mount is how they cannot disagree.
 *
 * WHY IT IS WORTH ASKING AT ALL. Run from the home screen this is a standalone window with the
 * service worker's cached shell behind it. Run from a browser tab on draft night it is a tab a
 * phone will evict while somebody is mid-auction, and coming back means finding the URL again.
 *
 * Everything about WHO sees it and WHAT they are told is in `domain/installPrompt.ts` and tested
 * there against real user-agent strings. This file is the box it goes in, plus the two pieces that
 * can only exist in a browser: the `beforeinstallprompt` event, and storage.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { dock } from './dock.js';
import {
  NEVER_KEY, SESSION_KEY, guideFor, isStandalone, readDevice, shouldOffer,
} from '../domain/installPrompt.js';

/**
 * The Chromium install event. Not in `lib.dom` — it is not a standard — so it is described here
 * rather than cast to `any` at the call site.
 */
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/*
 * PROBE, DO NOT FEATURE-DETECT. `typeof localStorage !== 'undefined'` is true inside a sandbox
 * that then throws on write — the same half-working failure `memoryStore` documents. And this one
 * must never take the app down: a browser refusing storage should cost the person a checkbox, not
 * a sign-in screen.
 */
function readFlag(store: 'local' | 'session', key: string): boolean {
  try {
    const s = store === 'local' ? window.localStorage : window.sessionStorage;
    return s.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(store: 'local' | 'session', key: string): void {
  try {
    const s = store === 'local' ? window.localStorage : window.sessionStorage;
    s.setItem(key, '1');
  } catch {
    /* A browser that will not remember gets asked again. Better than a crash. */
  }
}

export function AddToHome() {
  const [open, setOpen] = useState(false);
  const [never, setNever] = useState(false);
  const [guide, setGuide] = useState(() => guideFor({ family: 'other', browser: 'other' }));
  /* Held rather than rendered: the button appears only if the event actually fired. */
  const installer = useRef<InstallEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const device = readDevice(window.navigator.userAgent, window.navigator.maxTouchPoints ?? 0);
    setGuide(guideFor(device));
    setOpen(shouldOffer({
      device,
      standalone: isStandalone(window),
      dismissedForever: readFlag('local', NEVER_KEY),
      closedThisSession: readFlag('session', SESSION_KEY),
    }));

    /*
     * Chromium fires this instead of showing its own install bar, and only when the app actually
     * qualifies — manifest, icons, a service worker. Catching it lets the card offer a real button
     * on Android; not catching it means Chrome's own mini-infobar, which is easier to miss.
     */
    const onPrompt = (e: Event) => {
      e.preventDefault();
      installer.current = e as InstallEvent;
      setCanInstall(true);
    };
    const onInstalled = () => { setOpen(false); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const close = useCallback(() => {
    /*
     * The checkbox is read at CLOSE, not on change, so ticking it and then thinking better of it
     * costs nothing until the card is actually dismissed. Session-closed is written either way —
     * without it the Yahoo round trip reloads the page and the card comes straight back, which is
     * the fastest way to make a helpful thing annoying.
     */
    if (never) writeFlag('local', NEVER_KEY);
    writeFlag('session', SESSION_KEY);
    setOpen(false);
  }, [never]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  async function install() {
    const e = installer.current;
    if (!e) return;
    installer.current = null;
    setCanInstall(false);
    try {
      await e.prompt();
      await e.userChoice;
    } catch {
      /* The person closed the browser's own dialog. Nothing to say about it. */
    }
    close();
  }

  if (!open) return null;

  /* Docked — `position: fixed` inside a transformed `main`. See `src/ui/dock.tsx`. */
  return dock(
    <>
      <button type="button" className="a2hscrim" aria-label="Close" onClick={close} />
      <section className="a2hcard" role="dialog" aria-labelledby="a2htitle" aria-modal="false">
        <span className="a2hgrip" aria-hidden="true" />
        <h2 id="a2htitle" className="a2hhead">Keep this on your home screen</h2>
        <p className="a2hwhy">
          It opens like an app, fills the screen, and still works when the wifi in the draft room
          does not.
        </p>

        <p className="a2hwhere">{guide.title}</p>
        <ol className="a2hsteps">
          {guide.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
        {guide.note && <p className="a2hnote">{guide.note}</p>}

        {/* Only where the browser actually offered — see the event handler above. */}
        {canInstall && (
          <button type="button" className="btn primary a2hgo" onClick={() => void install()}>
            Install it now
          </button>
        )}

        <label className="a2hnever">
          <input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} />
          <span>Do not show this again</span>
        </label>

        <button type="button" className="btn a2hclose" onClick={close}>
          {never ? 'Close, and stop asking' : 'Not now'}
        </button>
      </section>
    </>
  );
}
